import { HttpService } from 'http/httpService'

export type V2Role = 'admission' | 'undated_admission' | 'non_admission' | null
export type V2PeriodType = 'Venue' | 'Gate'
export type V2Health = 'ok' | 'no_default_rule'

export interface V2CapacityRow {
    id: string
    name: string
    defaultAvailability: number | null
    reservationPeriodType: V2PeriodType
    role: V2Role
    group: 'visitor' | 'capacity'
    health: V2Health
    productsCount: number
    optionItemsCount: number
    exceptionsCount: number
}

export interface V2Exception {
    id: string
    name: string | null
    effectiveFrom: string | null
    effectiveTo: string | null
    weekdays: number[]
    timeFrom: string | null
    timeTo: string | null
    priority: number
    availability: number | null
}

export interface V2CapacityDetail extends V2CapacityRow {
    version: string
    exceptions: V2Exception[]
}

export interface V2Link {
    productListId: string | null
    amountUsed: number
}

export interface V2ProductMember {
    kind: 'product'
    id: string
    numericId: number
    name: Record<string, string>
    businessType: string | null
    isBundle: boolean
    enabled: boolean
    showFrom: string | null
    showTo: string | null
    validFrom: string | null
    validTo: string | null
    categoryIds: string[]
    links: V2Link[]
}

export interface V2OptionItemMember {
    kind: 'option_item'
    id: string
    name: Record<string, string>
    optionGroup: { uuid: string; name: Record<string, string> }
    links: V2Link[]
}

export type V2Member = V2ProductMember | V2OptionItemMember

export interface V2MembersPage {
    results: V2Member[]
    next: string | null
}

export interface V2CapacityWrite {
    name: string
    defaultAvailability: number | null
    reservationPeriodType?: V2PeriodType
}

export interface V2LinkChange {
    kind: 'product' | 'option_item'
    id: string
    productListId?: string | null
    amountUsed?: number
}

export interface V2LinkDelta {
    add?: V2LinkChange[]
    update?: V2LinkChange[]
    remove?: V2LinkChange[]
}

export interface V2LinkCounts {
    productsCount: number
    optionItemsCount: number
}

export interface V2TimeSlot {
    startTime: string
    endTime: string | null
}

export interface V2AppliedGroup {
    id: string
    name: string | null
    source: 'product' | 'category' | 'parentCategory' | 'default'
    categoryId: string | null
    weekdays: number[]
    availableFrom: string | null
    availableTo: string | null
    limitedToDates: boolean
    startOnly: boolean
    slots: V2TimeSlot[]
}

export interface V2ResolvedTimeSlots {
    window: 'slot' | 'day' | 'dependsOnCategory'
    reason:
        | 'product'
        | 'category'
        | 'parentCategory'
        | 'default'
        | 'categoryDisabled'
        | 'noGroup'
        | 'bundle'
        | 'dependsOnCategory'
    groups: V2AppliedGroup[]
}

export interface V2PickerRow {
    kind: 'product' | 'option_item'
    id: string
    timeSlots?: V2ResolvedTimeSlots | null
}

export interface V2VisitorCapacities {
    dated: V2CapacityRow
    undated: V2CapacityRow
    created: { dated: boolean; undated: boolean }
}

export class CapacityV2Error extends Error {
    constructor(
        public readonly status: number,
        public readonly code: string,
        message: string,
        public readonly details: Record<string, unknown> = {},
    ) {
        super(message)
        this.name = 'CapacityV2Error'
        Object.setPrototypeOf(this, CapacityV2Error.prototype)
    }
}

type Precondition = { ifMatch: string } | { ifNoneMatch: '*' }

const JSON_HEADERS = { 'Content-Type': 'application/json' }

export class CapacityV2Service {
    constructor(private httpService: HttpService, private backofficeEndpoint: string) {}

    private url(accountSlug: string, path: string): string {
        return `${this.backofficeEndpoint}api/v2/accounts/${accountSlug}/${path}`
    }

    private async call<T>(url: string, init: RequestInit = {}): Promise<T> {
        const response = await this.httpService.fetch(url, init)
        if (response.ok) {
            return (await response.json()) as T
        }
        let body: Record<string, unknown> = {}
        try {
            body = await response.json()
        } catch {
            body = {}
        }
        const code = typeof body.code === 'string' ? body.code : 'failed'
        const message = typeof body.message === 'string' ? body.message : `The request failed (${response.status}).`
        const details =
            typeof body.details === 'object' && body.details ? (body.details as Record<string, unknown>) : {}
        throw new CapacityV2Error(response.status, code, message, details)
    }

    listCapacities(accountSlug: string): Promise<V2CapacityRow[]> {
        return this.call(this.url(accountSlug, 'capacities/'))
    }

    getCapacity(accountSlug: string, capacityId: string): Promise<V2CapacityDetail> {
        return this.call(this.url(accountSlug, `capacities/${capacityId}/`))
    }

    putCapacity(
        accountSlug: string,
        capacityId: string,
        body: V2CapacityWrite,
        precondition: Precondition,
    ): Promise<V2CapacityDetail> {
        const headers: Record<string, string> =
            'ifMatch' in precondition ? { 'If-Match': precondition.ifMatch } : { 'If-None-Match': '*' }
        return this.call(this.url(accountSlug, `capacities/${capacityId}/`), {
            method: 'PUT',
            headers: { ...JSON_HEADERS, ...headers },
            body: JSON.stringify(body),
        })
    }

    async listMembers(accountSlug: string, capacityId: string): Promise<V2Member[]> {
        const members: V2Member[] = []
        let url: string | null = this.url(accountSlug, `capacities/${capacityId}/products/?page_size=200`)
        while (url) {
            const page: V2MembersPage = await this.call(url)
            members.push(...page.results)
            url = page.next
        }
        return members
    }

    patchMembers(accountSlug: string, capacityId: string, delta: V2LinkDelta): Promise<V2LinkCounts> {
        return this.call(this.url(accountSlug, `capacities/${capacityId}/products/`), {
            method: 'PATCH',
            headers: JSON_HEADERS,
            body: JSON.stringify(delta),
        })
    }

    async productTimeSlots(accountSlug: string, productId: string): Promise<V2ResolvedTimeSlots | null> {
        const page: { results: V2PickerRow[] } = await this.call(
            this.url(accountSlug, `capacity_products/?search=${encodeURIComponent(productId)}&include=time_slots`),
        )
        const product = page.results.find((row) => row.kind === 'product' && row.id === productId)
        return product?.timeSlots ?? null
    }

    ensureVisitorCapacities(accountSlug: string): Promise<V2VisitorCapacities> {
        return this.call(this.url(accountSlug, 'capacities/visitors/'), { method: 'PUT', headers: JSON_HEADERS })
    }
}
